/**
 * Hook for Ascensos de Grado (degree changes) report
 */

import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getMinisterDegreeChanges } from "../services/reports.service";
import type { MinisterDegreeChangeFilters, DegreeChangeSummary } from "../types";

const CURRENT_YEAR = new Date().getFullYear();

export function getDefaultDegreeChangeFilters(): MinisterDegreeChangeFilters {
  return {
    dateFrom: `${CURRENT_YEAR}-01-01`,
    dateTo: `${CURRENT_YEAR}-12-31`,
  };
}

export function useMinisterDegreeChangesReport() {
  const [filters, setFilters] = useState<MinisterDegreeChangeFilters>(
    getDefaultDegreeChangeFilters()
  );

  const { data: changes = [], isLoading, error } = useQuery({
    queryKey: ["ministerDegreeChanges", filters],
    queryFn: () => getMinisterDegreeChanges(filters),
    staleTime: 10 * 60 * 1000,  // 10 min — filter-keyed; each date range cached separately
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Aggregate: count by target degree
  const degreeToSummary = useMemo<DegreeChangeSummary[]>(() => {
    const map = new Map<string, DegreeChangeSummary>();
    changes.forEach((c) => {
      if (c.degree_to_id && c.degree_to_name) {
        const existing = map.get(c.degree_to_id);
        if (existing) {
          existing.count++;
        } else {
          map.set(c.degree_to_id, {
            degree_to_id: c.degree_to_id,
            degree_to_name: c.degree_to_name,
            count: 1,
          });
        }
      }
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [changes]);

  const updateFilter = (key: keyof MinisterDegreeChangeFilters, value: string) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const resetFilters = () => setFilters(getDefaultDegreeChangeFilters());

  return {
    changes,
    degreeToSummary,
    filters,
    isLoading,
    error,
    updateFilter,
    resetFilters,
  };
}
